import { CalendarX2 } from 'lucide-react';
import { Button } from '@finn-auto/design-system/atoms/button';
import { bottleneckNames } from './bottlenecks';
import { dayFmt } from './format';
import type { AtRiskLink, AtRiskReport, InterviewLink } from '../../services/interviewLinksApi';

function itemHeadline(item: AtRiskLink): string {
  if (item.risk === 'empty') {
    return `No bookable slot in the ${item.offeredRangeDays}-day window`;
  }
  return `Only ${item.slotsInWindow} slot${item.slotsInWindow === 1 ? '' : 's'} left in the ${item.offeredRangeDays}-day window`;
}

/**
 * Warns about active links whose waiting candidates see a (nearly) empty
 * booking page — BEFORE the candidate writes the "your link shows no slots"
 * email. Renders nothing when there is nothing to warn about: this is an
 * exception surface, not a status widget.
 */
export function AtRiskBanner({
  report,
  links,
  onEdit,
}: {
  report: AtRiskReport | undefined;
  links: InterviewLink[];
  onEdit: (link: InterviewLink) => void;
}) {
  if (!report) return null;

  // A total calendar-read outage must not render as a healthy board — silence
  // here reads as "all links fine" while candidates sit on dead pages, which
  // is a worse failure than the one this banner exists to catch.
  if (report.items.length === 0) {
    if (report.unreadableLinks > 0) {
      return (
        <p className="mb-4 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-xs text-gray-600">
          {report.unreadableLinks} link{report.unreadableLinks === 1 ? '' : 's'} with waiting
          candidates could not be checked — the interviewer calendars were not readable.
        </p>
      );
    }
    return null;
  }

  const linkById = new Map(links.map((l) => [l.id, l]));

  return (
    <section
      aria-label="Links at risk"
      className="mb-4 rounded-xl border border-[#EEC79A] bg-[#FCEEE2] p-4"
    >
      <header className="flex items-center gap-2">
        <CalendarX2 className="h-4 w-4 shrink-0 text-[#854F0B]" />
        <h2 className="text-sm font-semibold text-[#5C3608]">
          {report.items.length === 1
            ? '1 link may be unbookable for waiting candidates'
            : `${report.items.length} links may be unbookable for waiting candidates`}
        </h2>
      </header>

      <ul className="mt-3 space-y-2">
        {report.items.map((item) => {
          const blocked = bottleneckNames(item.perInterviewer);
          const link = linkById.get(item.linkId);
          return (
            <li
              key={item.linkId}
              className="flex flex-wrap items-center gap-3 rounded-lg border border-[#F6D8B8] bg-white p-3"
            >
              <div className="min-w-[220px] flex-1">
                <p className="text-sm font-medium text-gray-900">
                  {item.jobTitle} · {item.stageName}
                </p>
                <p className="mt-0.5 text-xs text-gray-600">
                  {itemHeadline(item)} · {item.pendingInvites} candidate
                  {item.pendingInvites === 1 ? '' : 's'} waiting
                  {item.firstBookable
                    ? ` · next free: ${dayFmt.format(new Date(item.firstBookable))}`
                    : ''}
                </p>
                {blocked.length > 0 ? (
                  <p className="mt-0.5 text-xs text-[#854F0B]">
                    {blocked.join(', ')} {blocked.length === 1 ? 'has' : 'have'} no free slot in the
                    window — extend the window, free up time, or swap the interviewer.
                  </p>
                ) : null}
              </div>
              {link ? (
                <Button size="sm" variant="outline" onClick={() => onEdit(link)}>
                  Edit link
                </Button>
              ) : null}
            </li>
          );
        })}
      </ul>
      {report.unreadableLinks > 0 ? (
        <p className="mt-2 text-xs text-[#854F0B]">
          {report.unreadableLinks} more link{report.unreadableLinks === 1 ? '' : 's'} could not be
          checked — calendars unreadable.
        </p>
      ) : null}
    </section>
  );
}
